// Ant Design theme — carries the Teaspoon brand into the antd components this
// admin app is built on.
//
// Before this, antd rendered on its stock blue (#1677ff) while everything
// styled by our own Sass was brand-coloured, so a primary Button sat blue next
// to a green .btn-primary on the same screen. antd never followed the template
// orange either — this was simply never wired up.
//
// The values come from the CSS custom properties published by
// style/scss/base/_base.scss, so Sass stays the single source of truth and
// changing $primary moves antd with it. The literals below are only a fallback
// for the case where the stylesheet has not been applied yet; they must match
// $primary / $primary-hover in scss/utils/_variables.scss.

import type { ThemeConfig } from 'antd';

const FALLBACK = {
  primary: '#1B7B36',
  primaryHover: '#19702F',
};

/**
 * Read a CSS custom property off :root.
 *
 * Returns the fallback when the value is missing or empty — which happens in
 * jsdom under `npm test`, and in the brief window before style-loader injects
 * the stylesheet. An empty string handed to antd would silently produce an
 * unthemed (blue) app rather than an obvious failure, so never pass it through.
 */
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name);
    return v && v.trim() ? v.trim() : fallback;
  } catch {
    return fallback;
  }
}

export function buildAntdTheme(): ThemeConfig {
  const colorPrimary = cssVar('--brand-primary', FALLBACK.primary);
  const colorPrimaryHover = cssVar('--brand-primary-hover', FALLBACK.primaryHover);

  return {
    token: {
      colorPrimary,
      // Our own `a { color: $primary }` already covers links outside antd;
      // this keeps links *inside* antd components (Table, Descriptions,
      // Typography) from staying blue.
      colorLink: colorPrimary,
      colorLinkHover: colorPrimaryHover,

      // Semantic colours are deliberately left at antd's defaults, the same
      // call made for $warning in the Sass pass: a warning or an error should
      // read as a warning or an error, not as brand decoration. Typography is
      // left alone as out of scope — antd keeps its own font stack.

      // Lift every antd overlay above the template's chrome.
      //
      // antd's popup base is 1000, and the template sets
      // `.sidebar, .sidebars { z-index: 1001 }` — one higher. So the sidebar
      // rendered ON TOP of the modal mask: a dialog would open with the nav
      // still bright, un-dimmed and clickable beside it. Raising the base is
      // the right end to fix, because lowering the sidebar would reshuffle the
      // template's own layering (overlay 99, footer 110, settings 9999) for
      // every non-antd surface too.
      //
      // Modal/Drawer take this value; Dropdown/Select/Tooltip/message sit above
      // it, so they stay above the modals they belong to.
      zIndexPopupBase: 1200,
    },
  };
}

export default buildAntdTheme;
